import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  Menu, Search, ChevronDown, ChevronLeft, ChevronRight, Plus, Lock,
  Home as HomeIcon, UserPlus, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../../contexts/AuthContext";
import { techniciansApi, jobsApi } from "../../lib/api";
import { getJobColor } from "../../data/dispatchMock";
import { getDisqualifyReasons } from "../../lib/techQualify";

const BRAND_BLUE = "#0077CC";
const BRAND_SKY = "#409EE6";

const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const sameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
const fmtShort = (d) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.length <= 16 ? `${iso}:00` : iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// Effective (job, tech, start, end) tuples — flattens multi-tech assignments[]
// down to legacy single-tech jobs, matching the desktop Dispatch Board logic.
function flattenAssignments(jobs) {
  const out = [];
  for (const j of jobs) {
    const assignments = j.assignments && j.assignments.length ? j.assignments : (j.techId ? [{ techId: j.techId, start: j.start, end: j.end }] : []);
    for (const a of assignments) {
      const start = a.start || j.start;
      if (!start) continue;
      out.push({ job: j, techId: a.techId, start, end: a.end || j.end });
    }
  }
  return out;
}

function divIcon(color, label) {
  return L.divIcon({
    html: `<div style="background:${color};color:#fff;font-weight:700;font-size:11px;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.25)">${label}</div>`,
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

const PRIORITY_PILL = {
  Urgent: { bg: "#FEE2E2", text: "#991B1B" },
  High: { bg: "#FEF3C7", text: "#92400E" },
  Normal: { bg: "#F1F5F9", text: "#475569" },
  Low: { bg: "#F1F5F9", text: "#64748B" },
};

export default function MobileDispatchBoard() {
  const [techs, setTechs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("Week"); // Day | Week | Month | Map
  const [anchor, setAnchor] = useState(() => new Date());
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [techsCollapsed, setTechsCollapsed] = useState(false);
  const [unassignedExpanded, setUnassignedExpanded] = useState(false);
  const [assigningJob, setAssigningJob] = useState(null);

  const load = () => {
    Promise.all([techniciansApi.list(), jobsApi.list()])
      .then(([t, j]) => { setTechs(t || []); setJobs(j || []); })
      .catch(() => toast.error("Could not load dispatch data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const onCreated = () => load();
    window.addEventListener("job-created", onCreated);
    return () => window.removeEventListener("job-created", onCreated);
  }, []);

  const filteredTechs = useMemo(() => {
    if (!query.trim()) return techs;
    const q = query.trim().toLowerCase();
    return techs.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [techs, query]);

  const visibleTechs = techsCollapsed ? filteredTechs.slice(0, 3) : filteredTechs;

  const flat = useMemo(() => flattenAssignments(jobs), [jobs]);
  const jobsFor = (techId, day) => flat.filter((x) => x.techId === techId && sameDay(new Date(x.start), day));

  const unassigned = useMemo(() => {
    const list = jobs.filter((j) => j.status === "Unassigned");
    if (!query.trim()) return list;
    const q = query.trim().toLowerCase();
    return list.filter((j) => (j.customer || "").toLowerCase().includes(q));
  }, [jobs, query]);
  const unassignedShown = unassignedExpanded ? unassigned : unassigned.slice(0, 3);

  const weekStart = startOfWeek(anchor);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const assignToTech = async (job, techId) => {
    const tech = techs.find((t) => t.id === techId);
    if (!tech) return;
    let startIso = job.start ? job.start.slice(0, 16) : null;
    let endIso = job.end ? job.end.slice(0, 16) : null;
    if (!startIso) {
      const base = new Date();
      base.setHours(9, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      const local = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      startIso = local(base);
      endIso = local(new Date(base.getTime() + (job.durationMin || 60) * 60000));
    }
    try {
      await jobsApi.patch(job.id, { status: "Scheduled", start: startIso, end: endIso, techId, tech: tech.name });
      toast.success(`Assigned to ${tech.name}`);
      setAssigningJob(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not assign job");
    }
  };

  return (
    <div className="mv md:hidden min-h-screen w-full" style={{ paddingBottom: "calc(5.5rem + env(safe-area-inset-bottom))" }} data-testid="mobile-dispatch-board">
      {/* Custom header */}
      <div
        className="px-4 pt-3 pb-4"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))", background: `linear-gradient(to bottom, ${BRAND_BLUE}, ${BRAND_SKY})` }}
        data-testid="mobile-dispatch-header"
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("open-mobile-drawer"))}
            data-testid="mobile-dispatch-menu-btn"
            aria-label="Open menu"
            className="h-10 w-10 rounded-full flex items-center justify-center active:bg-white/15"
          >
            <Menu className="h-6 w-6" color="#ffffff" />
          </button>
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/mobile-header-logo`} alt="Service Guru Pro" className="h-9 object-contain" />
          <button
            onClick={() => setSearchOpen((s) => !s)}
            data-testid="mobile-dispatch-search-btn"
            aria-label="Search"
            className="h-10 w-10 rounded-full flex items-center justify-center active:bg-white/15"
          >
            <Search className="h-5.5 w-5.5" color="#ffffff" />
          </button>
        </div>

        {searchOpen && (
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search technicians or customers…"
            data-testid="mobile-dispatch-search-input"
            className="mt-2 w-full h-10 px-3 rounded-full text-[13.5px] bg-white/95 text-slate-800 placeholder:text-slate-400 focus:outline-none"
          />
        )}

        <div className="flex items-center justify-between mt-3 gap-2">
          <h1 className="text-[26px] font-bold text-white leading-tight" data-testid="mobile-dispatch-title">Dispatch Board</h1>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("open-new-job"))}
            data-testid="mobile-dispatch-new-job-btn"
            className="shrink-0 h-9 px-4 rounded-full bg-[#1484F6] active:bg-[#0f6fd6] text-white text-[13px] font-bold inline-flex items-center gap-1"
          >
            <Plus className="h-4 w-4" /> New Job
          </button>
        </div>

        <button
          onClick={() => setDatePickerOpen((o) => !o)}
          data-testid="mobile-dispatch-date-range-btn"
          className="mt-1.5 inline-flex items-center gap-1 text-white/90 text-[13px] font-medium"
        >
          {fmtShort(weekStart)} — {fmtShort(addDays(weekStart, 6))}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>

        {datePickerOpen && (
          <div className="mt-2 bg-white rounded-xl p-2.5 flex items-center justify-between gap-2" data-testid="mobile-dispatch-date-picker">
            <button onClick={() => setAnchor((a) => addDays(a, -7))} data-testid="mobile-dispatch-prev-week" className="h-8 w-8 rounded-full flex items-center justify-center active:bg-slate-100">
              <ChevronLeft className="h-4 w-4 text-slate-600" />
            </button>
            <button onClick={() => setAnchor(new Date())} data-testid="mobile-dispatch-today-btn" className="text-[12.5px] font-semibold text-blue-600 px-2">Today</button>
            <input
              type="date"
              value={`${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, "0")}-${String(anchor.getDate()).padStart(2, "0")}`}
              onChange={(e) => e.target.value && setAnchor(new Date(`${e.target.value}T00:00:00`))}
              data-testid="mobile-dispatch-date-input"
              className="text-[12.5px] text-slate-700 border-none focus:outline-none"
            />
            <button onClick={() => setAnchor((a) => addDays(a, 7))} data-testid="mobile-dispatch-next-week" className="h-8 w-8 rounded-full flex items-center justify-center active:bg-slate-100">
              <ChevronRight className="h-4 w-4 text-slate-600" />
            </button>
          </div>
        )}

        {/* View toggle */}
        <div className="mt-3 bg-white rounded-full p-1 flex" data-testid="mobile-dispatch-view-toggle">
          {["Day", "Week", "Month", "Map"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              data-testid={`mobile-dispatch-view-${v.toLowerCase()}`}
              className={`flex-1 h-8 rounded-full text-[12.5px] font-semibold transition-colors ${
                view === v ? "bg-[#1484F6] text-white" : "bg-white text-slate-600"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
          <div className="h-8 w-8 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin mb-3" />
          <div className="text-[13px]">Loading dispatch board…</div>
        </div>
      ) : (
        <>
          {view === "Week" && (
            <WeekGrid
              techs={visibleTechs}
              weekDays={weekDays}
              jobsFor={jobsFor}
              collapsed={techsCollapsed}
              onToggleCollapse={() => setTechsCollapsed((c) => !c)}
              hasMore={filteredTechs.length > 3}
            />
          )}
          {view === "Day" && <DayView techs={filteredTechs} day={anchor} jobsFor={jobsFor} />}
          {view === "Month" && (
            <MonthView
              anchor={anchor}
              jobs={jobs}
              onPickDay={(d) => { setAnchor(d); setView("Day"); }}
            />
          )}
          {view === "Map" && <MapView techs={techs} jobs={jobs} day={anchor} />}

          {/* Unassigned Jobs */}
          <div className="mx-4 mt-4 bg-white rounded-2xl shadow-sm p-4" data-testid="mobile-dispatch-unassigned-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-slate-500" />
                <span className="font-bold text-slate-900 text-[14.5px]">Unassigned Jobs</span>
                <span className="bg-blue-600 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center" data-testid="mobile-dispatch-unassigned-count">
                  {unassigned.length}
                </span>
              </div>
              {unassigned.length > 3 && (
                <button onClick={() => setUnassignedExpanded((e) => !e)} data-testid="mobile-dispatch-unassigned-viewall" className="text-[12.5px] font-semibold text-blue-600">
                  {unassignedExpanded ? "Show less" : "View All"} {!unassignedExpanded && ">"}
                </button>
              )}
            </div>

            {unassigned.length === 0 ? (
              <div className="text-center text-[13px] text-slate-400 py-4">All jobs are assigned 🎉</div>
            ) : (
              <div className="space-y-3">
                {unassignedShown.map((job) => (
                  <UnassignedCard key={job.id} job={job} onAssign={() => setAssigningJob(job)} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {assigningJob && (
        <AssignSheet
          job={assigningJob}
          techs={techs}
          onClose={() => setAssigningJob(null)}
          onAssign={(techId) => assignToTech(assigningJob, techId)}
        />
      )}
    </div>
  );
}

function TechAvatar({ tech, size = 40 }) {
  if (tech.avatarUrl) {
    return <img src={`${process.env.REACT_APP_BACKEND_URL}${tech.avatarUrl}`} alt={tech.name} className="rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
      style={{ width: size, height: size, backgroundColor: tech.color || "#64748B", fontSize: size * 0.34 }}
    >
      {tech.avatar || (tech.name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

function JobChip({ job, start }) {
  const c = getJobColor(job.type);
  return (
    <div
      data-testid={`mobile-dispatch-job-${job.id}`}
      className="rounded-lg px-1.5 py-1.5 w-full"
      style={{ backgroundColor: c.bg, border: `1px solid ${c.border}22` }}
    >
      <div className="text-[10.5px] font-bold truncate" style={{ color: "#0f172a" }}>{fmtTime(start)}</div>
      <div className="text-[10px] font-medium truncate text-slate-700">{job.customer}</div>
      <div className="text-[10px] font-bold truncate text-blue-600">${(job.value || 0).toLocaleString()}</div>
    </div>
  );
}

function WeekGrid({ techs, weekDays, jobsFor, collapsed, onToggleCollapse, hasMore }) {
  const COL_W = 92;
  return (
    <div className="mx-4 mt-4 bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="mobile-dispatch-week-grid">
      <div className="overflow-x-auto">
        <div className="flex" style={{ minWidth: 76 + weekDays.length * COL_W }}>
          <div className="w-[76px] shrink-0 sticky left-0 bg-white z-10 border-r border-slate-100">
            <div className="h-11 flex flex-col items-center justify-center border-b border-slate-100">
              <ChevronLeft className="h-3 w-3 text-slate-400" />
              <span className="text-[11px] font-semibold text-slate-500">Techs</span>
            </div>
          </div>
          {weekDays.map((d) => (
            <div key={d.toISOString()} className="shrink-0 border-r border-slate-100 last:border-0" style={{ width: COL_W }}>
              <div className="h-11 flex flex-col items-center justify-center border-b border-slate-100">
                <span className="text-[10.5px] font-semibold text-slate-500">{DAY_ABBR[d.getDay()]}</span>
                <span className="text-[13px] font-bold text-slate-900">{d.getDate()}</span>
              </div>
            </div>
          ))}
        </div>

        {techs.map((t) => (
          <div key={t.id} className="flex border-t border-slate-100" data-testid={`mobile-dispatch-tech-row-${t.id}`}>
            <div className="w-[76px] shrink-0 sticky left-0 bg-white z-10 border-r border-slate-100 flex flex-col items-center py-2.5 gap-1">
              <TechAvatar tech={t} size={36} />
              <div className="text-[10.5px] font-semibold text-slate-700 text-center leading-tight px-0.5">{(t.name || "").split(" ")[0]}</div>
              <div className="text-[9.5px] text-slate-400 text-center leading-tight px-0.5">{(t.name || "").split(" ").slice(1).join(" ")}</div>
            </div>
            {weekDays.map((d) => {
              const dayJobs = jobsFor(t.id, d);
              return (
                <div key={d.toISOString()} className="shrink-0 border-r border-slate-100 last:border-0 p-1 flex flex-col gap-1 justify-center items-center" style={{ width: COL_W, minHeight: 64 }}>
                  {dayJobs.length === 0 ? (
                    <span className="text-slate-300 text-[13px]">—</span>
                  ) : (
                    dayJobs.map((x) => <JobChip key={x.job.id + x.start} job={x.job} start={x.start} />)
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {hasMore && (
        <button onClick={onToggleCollapse} data-testid="mobile-dispatch-collapse-techs" className="w-full py-2 flex items-center justify-center border-t border-slate-100 active:bg-slate-50">
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? "" : "rotate-180"}`} />
        </button>
      )}
    </div>
  );
}

function DayView({ techs, day, jobsFor }) {
  return (
    <div className="mx-4 mt-4 space-y-3" data-testid="mobile-dispatch-day-view">
      {techs.map((t) => {
        const dayJobs = jobsFor(t.id, day).sort((a, b) => a.start.localeCompare(b.start));
        return (
          <div key={t.id} className="bg-white rounded-2xl shadow-sm p-3.5" data-testid={`mobile-dispatch-day-tech-${t.id}`}>
            <div className="flex items-center gap-2.5 mb-2.5">
              <TechAvatar tech={t} size={32} />
              <span className="font-semibold text-slate-900 text-[13.5px]">{t.name}</span>
            </div>
            {dayJobs.length === 0 ? (
              <div className="text-[12.5px] text-slate-400 pl-1">No jobs scheduled</div>
            ) : (
              <div className="space-y-1.5">
                {dayJobs.map((x) => {
                  const c = getJobColor(x.job.type);
                  return (
                    <div key={x.job.id} className="rounded-lg px-3 py-2 flex items-center justify-between" style={{ backgroundColor: c.bg }}>
                      <div>
                        <div className="text-[12px] font-bold text-slate-900">{fmtTime(x.start)}</div>
                        <div className="text-[11.5px] text-slate-600">{x.job.customer}</div>
                      </div>
                      <div className="text-[12px] font-bold text-blue-600">${(x.job.value || 0).toLocaleString()}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ anchor, jobs, onPickDay }) {
  const flat = useMemo(() => flattenAssignments(jobs), [jobs]);
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const countFor = (d) => flat.filter((x) => sameDay(new Date(x.start), d)).length;
  const today = new Date();

  return (
    <div className="mx-4 mt-4 bg-white rounded-2xl shadow-sm p-3" data-testid="mobile-dispatch-month-view">
      <div className="text-center font-bold text-slate-900 text-[14px] mb-2">
        {anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_ABBR.map((d) => <div key={d} className="text-center text-[10.5px] font-semibold text-slate-400">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const inMonth = d.getMonth() === anchor.getMonth();
          const count = countFor(d);
          return (
            <button
              key={d.toISOString()}
              onClick={() => onPickDay(d)}
              data-testid={`mobile-dispatch-month-day-${d.toISOString().slice(0, 10)}`}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center gap-0.5 ${sameDay(d, today) ? "bg-blue-50" : ""}`}
            >
              <span className={`text-[12px] ${inMonth ? "text-slate-800 font-medium" : "text-slate-300"}`}>{d.getDate()}</span>
              {count > 0 && (
                <span className="text-[9px] font-bold text-white bg-blue-600 rounded-full min-w-[14px] px-1 leading-[14px]">{count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MapView({ techs, jobs, day }) {
  const flat = useMemo(() => flattenAssignments(jobs), [jobs]);
  const todays = flat.filter((x) => sameDay(new Date(x.start), day) && x.job.coords);
  const center = todays.length ? [todays[0].job.coords.lat, todays[0].job.coords.lng] : [33.4484, -112.074];

  return (
    <div className="mx-4 mt-4 bg-white rounded-2xl shadow-sm overflow-hidden" style={{ height: 340 }} data-testid="mobile-dispatch-map-view">
      {todays.length === 0 ? (
        <div className="h-full flex items-center justify-center text-[13px] text-slate-400">No jobs scheduled for this day</div>
      ) : (
        <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {todays.map((x) => {
            const tech = techs.find((t) => t.id === x.techId);
            const color = tech?.color || "#2563EB";
            return (
              <Marker key={x.job.id} position={[x.job.coords.lat, x.job.coords.lng]} icon={divIcon(color, tech?.avatar || "?")}>
                <Popup>
                  <strong>{x.job.customer}</strong><br />
                  {fmtTime(x.start)} · {x.job.type}<br />
                  {tech?.name}
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      )}
    </div>
  );
}

function UnassignedCard({ job, onAssign }) {
  const navigate = useNavigate();
  const pr = PRIORITY_PILL[job.priority] || PRIORITY_PILL.Normal;
  return (
    <div
      onClick={() => navigate(`/mobile/jobs/${job.id}`, { state: { from: "/dispatch" } })}
      role="button"
      className="rounded-xl border border-slate-100 p-3 cursor-pointer active:bg-slate-50"
      data-testid={`mobile-dispatch-unassigned-card-${job.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
          <HomeIcon className="h-4.5 w-4.5 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900 text-[13.5px] truncate">{job.customer}</div>
          <div className="text-[12px] text-slate-500 truncate">{job.address}{job.city ? `, ${job.city}` : ""}</div>
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10.5px] font-medium">{job.type}</span>
        </div>
        <div className="text-right shrink-0">
          <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold" style={{ backgroundColor: pr.bg, color: pr.text }}>
            {job.priority || "Normal"}
          </span>
          {job.start && (
            <div className="text-[11px] text-slate-500 mt-1">
              {new Date(job.start).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {fmtTime(job.start)}
            </div>
          )}
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onAssign(); }}
        data-testid={`mobile-dispatch-assign-btn-${job.id}`}
        className="mt-3 w-full h-10 rounded-full bg-[#1484F6] active:bg-[#0f6fd6] text-white text-[13px] font-semibold flex items-center justify-center gap-1.5"
      >
        <UserPlus className="h-4 w-4" /> Assign to Technician
      </button>
    </div>
  );
}

function AssignSheet({ job, techs, onClose, onAssign }) {
  const [overrideTech, setOverrideTech] = useState(null); // { tech, reasons } | null
  const [overrideText, setOverrideText] = useState("");

  const handleRowClick = (t, reasons) => {
    if (reasons.length === 0) {
      onAssign(t.id);
    } else {
      setOverrideText("");
      setOverrideTech({ tech: t, reasons });
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-slate-900/50" data-testid="mobile-dispatch-assign-sheet" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-t-2xl max-h-[75vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <div className="font-bold text-slate-900 text-[14.5px]">Assign to Technician</div>
            <div className="text-[12px] text-slate-500 truncate">{job.customer}</div>
          </div>
          <button onClick={onClose} data-testid="mobile-dispatch-assign-close" className="h-8 w-8 rounded-full flex items-center justify-center active:bg-slate-100">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-2">
          {techs.length === 0 ? (
            <div className="py-6 text-center text-[13px] text-slate-400">No technicians available</div>
          ) : (
            techs.map((t) => {
              const reasons = getDisqualifyReasons(t, job);
              const disqualified = reasons.length > 0;
              return (
                <button
                  key={t.id}
                  onClick={() => handleRowClick(t, reasons)}
                  data-testid={disqualified ? `mobile-dispatch-assign-tech-disqualified-${t.id}` : `mobile-dispatch-assign-tech-${t.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl active:bg-slate-50 ${disqualified ? "opacity-45" : ""}`}
                >
                  <TechAvatar tech={t} size={34} />
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-slate-900 truncate">{t.name}</div>
                    <div className="text-[11.5px] text-slate-400">{disqualified ? "Not qualified" : t.status}</div>
                  </div>
                  {disqualified && <AlertTriangle className="h-4 w-4 text-slate-400 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </div>

      {overrideTech && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => { e.stopPropagation(); setOverrideTech(null); }}
          data-testid="mobile-dispatch-override-backdrop"
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[320px] rounded-2xl bg-white shadow-2xl p-4" data-testid={`mobile-dispatch-override-popup-${overrideTech.tech.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4.5 w-4.5 text-amber-500 shrink-0" />
              <div className="text-[14px] font-bold text-slate-900">Can't assign {overrideTech.tech.name}</div>
            </div>
            <ul className="text-[12.5px] text-slate-600 space-y-1 mb-3 pl-1">
              {overrideTech.reasons.map((r) => <li key={r}>• {r}</li>)}
            </ul>
            <input
              type="text"
              value={overrideText}
              onChange={(e) => setOverrideText(e.target.value)}
              placeholder="Type 'Override' to still assign this technician"
              data-testid={`mobile-dispatch-override-input-${overrideTech.tech.id}`}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 mb-3"
            />
            <div className="flex items-center gap-2">
              <button onClick={() => setOverrideTech(null)} data-testid={`mobile-dispatch-override-cancel-${overrideTech.tech.id}`} className="flex-1 h-9 rounded-full border border-slate-200 text-slate-600 text-[13px] font-semibold">
                Cancel
              </button>
              <button
                onClick={() => { onAssign(overrideTech.tech.id); setOverrideTech(null); }}
                disabled={overrideText.trim().toLowerCase() !== "override"}
                data-testid={`mobile-dispatch-override-save-${overrideTech.tech.id}`}
                className="flex-1 h-9 rounded-full bg-blue-600 text-white text-[13px] font-bold disabled:opacity-40"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
